import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { nanoid } from 'nanoid';
import type { ProcessRunStatus } from '@vivport/backend/types';

/** Client-side background work tracked in the UI queue (not server ProcessRuns). */
export interface AsyncTask {
  id: string;
  title: string;
  description?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress?: number; // 0-100 while processing
  createdAt: Date;
  completedAt?: Date;
  error?: string;
  contentType?:
    | 'photo'
    | 'recording'
    | 'ANCHOR'
    | 'EVENT'
    | 'DOCUMENT'
    | 'NEO_CHAT'
    | 'FILE'
    | 'URL'
    | 'MESSAGE'
    | 'PROCESS_RUN';
  contentId?: string | number;
  /** The server-side status of the PROCESS_RUN this task mirrors; drives the Cancel action. */
  runStatus?: ProcessRunStatus;
  payload?: any;
}

interface AsyncStore {
  tasks: AsyncTask[];

  addTask: (taskData: Omit<AsyncTask, 'id' | 'createdAt'>) => string;
  updateTask: (id: string, updates: Partial<AsyncTask>) => void;
  removeTask: (id: string) => void;
  getActiveTasks: () => AsyncTask[];
  clearCompletedTasks: () => void;
  autoRemoveCompletedTask: (id: string, delay?: number) => void;
}

export const useAsyncStore = create<AsyncStore>()(
  persist(
    (set, get) => ({
      tasks: [],

      addTask: (taskData) => {
        const id = nanoid(10);
        const newTask: AsyncTask = {
          ...taskData,
          id,
          createdAt: new Date(),
        };

        set((state) => ({
          tasks: [...state.tasks, newTask],
        }));

        return id;
      },

      updateTask: (id, updates) => {
        set((state) => {
          let didChange = false;
          const nextTasks = state.tasks.map((task) => {
            if (task.id !== id) {
              return task;
            }

            const isTerminalStatusUpdate =
              updates.status !== undefined && ['completed', 'failed'].includes(updates.status);
            let hasFieldChange = false;
            for (const [key, value] of Object.entries(updates)) {
              const taskValue = (task as unknown as Record<string, unknown>)[key];
              if (!Object.is(taskValue, value)) {
                hasFieldChange = true;
                break;
              }
            }

            if (!hasFieldChange && !isTerminalStatusUpdate) {
              return task;
            }

            didChange = true;
            return {
              ...task,
              ...updates,
              completedAt: isTerminalStatusUpdate ? new Date() : task.completedAt,
            };
          });

          if (!didChange) {
            return state;
          }

          return { tasks: nextTasks };
        });

        if (updates.status === 'completed') {
          get().autoRemoveCompletedTask(id, 10000);
        }
      },

      removeTask: (id) => {
        set((state) => ({
          tasks: state.tasks.filter((task) => task.id !== id),
        }));
      },

      getActiveTasks: () => {
        return get().tasks.filter((task) => task.status === 'pending' || task.status === 'processing');
      },

      clearCompletedTasks: () => {
        set((state) => ({
          tasks: state.tasks.filter((task) => task.status === 'pending' || task.status === 'processing'),
        }));
      },

      autoRemoveCompletedTask: (id, delay = 10000) => {
        setTimeout(() => {
          const task = get().tasks.find((t) => t.id === id);
          if (task && task.status === 'completed') {
            get().removeTask(id);
          }
        }, delay);
      },
    }),
    {
      name: 'async-tasks-storage',
      partialize: (state) => ({
        tasks: state.tasks.map((task) => ({
          ...task,
          createdAt: task.createdAt.toISOString(),
          completedAt: task.completedAt?.toISOString(),
        })),
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.tasks = state.tasks.map((task: any) => ({
            ...task,
            createdAt: new Date(task.createdAt),
            completedAt: task.completedAt ? new Date(task.completedAt) : undefined,
          }));

          const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
          state.tasks = state.tasks.filter((task: AsyncTask) => {
            if (['completed', 'failed'].includes(task.status) && task.completedAt && task.completedAt < oneHourAgo) {
              return false;
            }
            return true;
          });
        }
      },
    }
  )
);
